import React, { createContext, useContext, useMemo } from 'react'

import { get } from 'lodash'
import {
  DefaultTheme,
  ThemeProvider,
  createGlobalStyle,
  css,
} from 'styled-components'
import { palette, mediaQueries, transitions } from '@centivo/react-components'

import { client } from 'src/api'
import { AppConfig, CONFIG_KEYS, PlanNetworkMap } from 'src/api/types'
import { useDidMount, useSmartState } from 'src/hooks'
import { TServiceLine } from 'src/shared/types'
import { getStore, usePersistStore } from 'src/context/functions/globalStore'
import { isSuccessResponse } from 'src/api/functions'
import { Context } from './types'
import { parseConfig } from './parseAppConfig'

export const GlobalContext = createContext<Context | null>(null)

const store = getStore()

const initialConfig =
  window.APP_CONFIG ||
  CONFIG_KEYS.map(key => get(process.env, key)) ||
  store?.appConfig

export const GlobalContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [appConfig, setAppConfig] = useSmartState<AppConfig>(initialConfig)
  const [serviceLines, setServiceLines] = useSmartState<
    | {
        doctor: TServiceLine[]
        facility: TServiceLine[]
      }
    | undefined
  >(store?.serviceLines)
  const [planNetworkMaps, setPlanNetworks] = useSmartState<PlanNetworkMap[]>(
    store?.planNetworkMaps || []
  )
  const [selectedPlan, setSelectedPlan] = useSmartState<string | null>(
    store?.selectedPlan || null
  )
  const [isLoading, setIsLoading] = useSmartState(true)

  usePersistStore({
    planNetworkMaps,
    selectedPlan: selectedPlan || undefined,
    serviceLines,
    appConfig,
  })
  useDidMount(async () => {
    const data = await client.getServiceLines()
    const response = await client.getOESiteConfig(
      window.location.origin.replace(/https?:\/\//, '')
    )
    const isPlanManager = isSuccessResponse(response)
    const appData = isPlanManager ? response.data : initialConfig

    const networks = appData.REACT_APP_PROVIDER_NETWORK_MAPS
    const oePlanNetworks = appData.REACT_APP_PLAN_NETWORKS
    const networkData = await client.getOeNetworks(
      networks || oePlanNetworks,
      appData.REQUIRE_VPC_MODULE
    )

    if (oePlanNetworks) {
      client.setPlanNetworks(store?.selectedPlan)
      setPlanNetworks(networkData as PlanNetworkMap[])
    }

    setServiceLines(data)
    setIsLoading(false)

    setAppConfig(appData)
  })

  const value = useMemo((): Context => {
    const onSelectPlan = (name: string) => {
      client.setPlanNetworks(name)

      setSelectedPlan(name)
    }
    const clearSelectedPlan = () => {
      setSelectedPlan(null)
    }

    return [
      {
        serviceLines,
        planNetworkMaps,
        selectedPlan,
        isLoading,
        appConfig: parseConfig(appConfig),
      },
      {
        onSelectPlan,
        clearSelectedPlan,
      },
    ]
  }, [
    serviceLines,
    planNetworkMaps,
    selectedPlan,
    isLoading,
    setSelectedPlan,
    appConfig,
  ]) as Context

  const theme: DefaultTheme = {
    template: value[0].appConfig.template,

    screenWidth: css`
      width: 100%;

      @media ${mediaQueries.desktop} {
        width: calc(1440px + 4rem);
        max-width: 90%;
      }
    `,

    palette,
    mediaQueries,
    transitions,
  }

  const SliceFonts = createGlobalStyle`
    body, body * {
      font-family: "roc-grotesk", sans-serif !important;
    }
  `

  return (
    <GlobalContext.Provider value={value}>
      {(appConfig.REACT_APP_TEMPLATE === 'template-4' ||
        appConfig.REACT_APP_TEMPLATE === 'template-eligibility') && (
        <SliceFonts />
      )}
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </GlobalContext.Provider>
  )
}

export const useGlobalState = () => useContext(GlobalContext) as Context

export const useSliceTemplate = (): boolean => {
  const [{ appConfig }] = useGlobalState()

  return (
    appConfig.template === 'template-4' ||
    appConfig.template === 'template-eligibility'
  )
}
